import { useTranslation } from "react-i18next";
import useWindowSize from '../../Helpers/useWindowSize';
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { Link, useNavigate } from "react-router-dom";

const CheckIcon = () => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="#c1e7c3" 
        stroke-width="2" 
        stroke-linecap="round" 
        stroke-linejoin="round"
        aria-hidden="true"
        >
        <path d="M3 12l5 5l9-9" />
        <path d="M9 12l5 5l9-9" />
        </svg>
);

const SpinnerIcon = () => (
     <svg 
    className="spinner" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true" /* Hide from screen readers */
  >
    {/* The faint track circle in the background */}
    <circle 
      cx="12" 
      cy="12" 
      r="10" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      opacity="0.2"
    />
    
    {/* The main spinning arc */}
    <path 
      d="M12 2 a10 10 0 0 1 10 10" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round"
    />
  </svg>
);

const MagnifyingGlassIcon = () => (
    <div className="main-icon-background">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 21L16.65 16.65" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    </div>
);

const HiddenIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 12C3 12 5 8 12 8C19 8 21 12 21 12M3 12C3 12 5 16 12 16C19 16 21 12 21 12M3 12H21" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const WarningIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const PendingIcon = () => (
     <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="#4B5563" strokeWidth="2" />
    </svg>
);

interface propTypes {
    isWhiteLabel: boolean // base64 image of wcag
}

// component to render loader
const CustomLoader = (props: propTypes) => {

    const { t } = useTranslation();

    const { windowHeight } = useWindowSize();

    const { isWhiteLabel } = props;

    const { scaningProcessPer, websiteURL, standard, token } = useSelector((store: StoreProptypes) => store);

    const [scale, setScale] = useState(1);

    const navigate = useNavigate();

    const handleExit = () => {
        navigate(token ? `/?token=${token}` : '/');
    };

    useEffect(() => {
        if (windowHeight) {
            const temp = (windowHeight * .44) / 480;
            setScale(temp);
        }
    }, [windowHeight])

    const steps = [
        {
            title: t("Starting Scan"),
            description: t("Collecting website data, detecting page structure, and initializing the analysis engine"),
            subTitle: t("Scanning")
        },
        {
            title: t("Fetching Content"),
            description: t("Gathering images, text, links, and code elements to prepare for scanning"),
            subTitle: t("Fetching")
        },
        {
            title: t("Running Accessibility Checks"),
            description: t("Evaluating visuals, navigation, content clarity, and code to identify accessibility issues"),
            subTitle: t("Running")
        },
        {
            title: t("Preparing Your Report"),
            description: t("Summarizing findings, determining your accessibility score, and organizing recommendations"),
            subTitle: t("Preparing")
        }
    ];

    const checkItems = [
        { id: 1, icon: <CheckIcon />, text: t("Labels for interactive elements") },
        { id: 2, icon: <HiddenIcon />, text: t("Hidden elements ignored by keyboard") },
        { id: 3, icon: <CheckIcon />, text: t("Color contrast is sufficient") },
        { id: 4, icon: <WarningIcon />, text: t("Alt text for images is descriptive") },
        { id: 5, icon: <CheckIcon />, text: t("Keyboard navigation is logical") }
    ];

    const scrollingItems = [...checkItems, ...checkItems];

    const [currentStep, setCurrentStep] = useState(0);

    const weights = [10, 10, 70, 10]; // total = 100

    useEffect(() => {
       const cumulativeWeights = weights.reduce<number[]>((acc, w, i) => {
        acc[i] = (acc[i - 1] || 0) + w; // sum up to this step
        return acc;
        }, []);

        // Find current step based on scanning progress
        const step = cumulativeWeights.findIndex(cumWeight => scaningProcessPer < cumWeight) + 1;

        setCurrentStep(step > weights.length ? weights.length : step);
    }, [scaningProcessPer]);


    /**
     * Determines the status of a step based on its index compared to the current active step.
     * @param {number} stepIndex - The index of the step to check.
     * @returns {'completed' | 'in-progress' | 'pending'} - The status of the step.
     */
    const getStepStatus = (stepIndex:number) => {
        if (stepIndex + 1 < currentStep) {
            return 'completed';
        }
        if (stepIndex + 1 === currentStep) {
            return 'in-progress';
        }
        return 'pending';
    };

    const getStepProgress = (stepIndex: number) => {
        if (stepIndex + 1 < currentStep) return 100;
        if (stepIndex + 1 === currentStep) return scaningProcessPer % 25 * 4; // map 0-25 → 0-100%
        return 0;
    };

    return (
        <>
          <div className="accessibility-checker d-flex vh-100">
      {/* Sidebar */}
      <div className="checker-sidebar p-4 text-white d-flex flex-column justify-content-between">
        <div>
          {/* <h5 className="fw-bold mb-3">{t('Accessibility Report')}</h5> */}
          <h6 className="fs-3 website-ellipsis">
            {t('Scanning')} <span className="website-ellipsis">{websiteURL}</span>
          </h6>

          <p className="small mt-3 text-tertiary">
            {t('Your website is being evaluated against {{standard}} & other international accessibility standards',{ 
                standard: standard 
            })}.
          </p>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>{t('Progress')}</span>
              <span>{scaningProcessPer}%</span>
            </div>
            <div className="progress" style={{ height: "8px" }}>
              <motion.div
                className="progress-bar progress-bar-striped"
                role="progressbar"
                style={{backgroundColor: '#c1e7c3'}}
                initial={{ width: "0%" }}
                animate={{ width: `${scaningProcessPer}%` }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              />
            </div>
          </div>

          {/* Dynamic Step List */}
          <div className="progress-container mt-3" role="status" aria-live="polite">
            <ul className="progress-steps-list">
                {steps.map((step, index) => {
                    const status = getStepStatus(index);
                    const stepProgress = getStepProgress(index);

                    return (
                    <li key={index} className={`progress-step ${status}`}>
                        <div className="step-icon">
                        {status === "completed" && <CheckIcon />}
                        {status === "in-progress" && <SpinnerIcon />}
                        {status === "pending" && <PendingIcon />}
                        </div>
                        <div className="step-content">
                        <h3 className="step-title">{step.title}</h3>
                         <AnimatePresence mode="wait">
                            {status === "in-progress" && (
                            <motion.p
                                key="desc"
                                className="step-description"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                            >
                                {step.description}
                            </motion.p>
                            )}
                        </AnimatePresence>
                        </div>
                    </li>
                    );
                })}
            </ul>
        </div>

        </div>

        <div className="text-center small">
            {!isWhiteLabel && (
                <a href="https://www.skynettechnologies.com/report-accessibility-problem" target="_blank" className="text-decoration-none">
                    {t('Need support')}?{" "}
                </a>  
            )}
            <Link to={token ? `/?token=${token}` : '/'} onClick={handleExit} className="text-decoration-none exit-scan">
                {t('Exit Scan')}
            </Link>
        </div>
      </div>

      {/* Right Section */}
      <div className="checker-content flex-grow-1 d-flex flex-column justify-content-center align-items-center">
                <div className="cat-container">
                <div className="all-wrap">
                    <div className="scale-wrapper" style={{ transform: `scale(${scale})` }}>
                        <div className="all">
                            <div className="yarn"></div>
                            <div className="cat-wrap">
                                <div className="cat">
                                    <div className="cat-upper">
                                        <div className="cat-leg"></div>
                                        <div className="cat-leg"></div>
                                        <div className="cat-head">
                                            <div className="cat-ears">
                                                <div className="cat-ear"></div>
                                                <div className="cat-ear"></div>
                                            </div>
                                            <div className="cat-face">
                                                <div className="cat-eyes"></div>
                                                <div className="cat-mouth"></div>
                                                <div className="cat-whiskers"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="cat-lower-wrap">
                                        <div className="cat-lower">
                                            <div className="cat-leg">
                                                <div className="cat-leg">
                                                    <div className="cat-leg">
                                                        <div className="cat-leg">
                                                            <div className="cat-leg">
                                                                <div className="cat-leg">
                                                                    <div className="cat-leg">
                                                                        <div className="cat-leg">
                                                                            <div className="cat-leg">
                                                                                <div className="cat-leg">
                                                                                    <div className="cat-leg">
                                                                                        <div className="cat-leg">
                                                                                            <div className="cat-leg">
                                                                                                <div className="cat-leg">
                                                                                                    <div className="cat-leg">
                                                                                                        <div className="cat-leg">
                                                                                                            <div className="cat-paw"></div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="cat-leg">
                                                <div className="cat-leg">
                                                    <div className="cat-leg">
                                                        <div className="cat-leg">
                                                            <div className="cat-leg">
                                                                <div className="cat-leg">
                                                                    <div className="cat-leg">
                                                                        <div className="cat-leg">
                                                                            <div className="cat-leg">
                                                                                <div className="cat-leg">
                                                                                    <div className="cat-leg">
                                                                                        <div className="cat-leg">
                                                                                            <div className="cat-leg">
                                                                                                <div className="cat-leg">
                                                                                                    <div className="cat-leg">
                                                                                                        <div className="cat-leg">
                                                                                                            <div className="cat-paw"></div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="cat-tail">
                                                <div className="cat-tail">
                                                    <div className="cat-tail">
                                                        <div className="cat-tail">
                                                            <div className="cat-tail">
                                                                <div className="cat-tail">
                                                                    <div className="cat-tail">
                                                                        <div className="cat-tail">
                                                                            <div className="cat-tail">
                                                                                <div className="cat-tail">
                                                                                    <div className="cat-tail">
                                                                                        <div className="cat-tail">
                                                                                            <div className="cat-tail">
                                                                                                <div className="cat-tail">
                                                                                                    <div className="cat-tail">
                                                                                                        <div className="cat-tail -end"></div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="main-checks-container">
            
            <h2 className="main-checks-title">{steps[currentStep - 1]?.subTitle || 'Scanning'}....</h2>
            
            {/* This div acts as a viewport, hiding the overflowing content */}
            <div className="list-viewport">
                {/* This list contains the duplicated items and is the element we animate */}
                <ul className="scrolling-list">
                    {scrollingItems.map((item, index) => (
                        <li key={index} className="check-item">
                            <span className="check-item-icon">{item.icon}</span>
                            <span>{item.text}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
      </div>
    </div>

        
            {/* 
            
            <div className='fullscreen-loader-wrapper' role="status" aria-live="polite" aria-atomic="true">
                <div className='fullscreen-loader'>
                    <div className='loader-wrapper'>
                        <div className='loader-text'> <strong> {t("loader-text")} </strong> </div>
                        <div className="linear-progress">
                            <div className="bar bar1"></div>
                            <div className="bar bar2"></div>
                        </div>
                    </div>
                </div>
            </div> */}
        </>
    )
}

export default CustomLoader